import { NavLink } from 'react-router-dom';
import { Home, Receipt, LineChart, Banknote, Settings, Wallet, CalendarClock, Percent, Building2, Repeat2, Users, ShoppingBag, Coins, ScanLine, FileText } from 'lucide-react';
import EntitySelector from './EntitySelector.jsx';
import { useEntityScope } from '../hooks/useEntityScope.js';

export default function Sidebar() {
  const { isLoading, isGroup, memberships, activeMembership } = useEntityScope();

  const groupEligible = memberships.filter((m) => m.include_in_group);
  // Refacturation only makes sense once at least 2 entities are flagged for
  // group consolidation — same threshold as the "Vue groupe" option in
  // EntitySelector.
  const showRefacturation = !isLoading && groupEligible.length >= 2;
  // Ventes (Shopify) — visible if the active entity has Shopify configured;
  // in group scope, visible as soon as one group-eligible entity has it.
  const showVentes = !isLoading && (
    isGroup ? groupEligible.some((m) => m.has_shopify) : Boolean(activeMembership?.has_shopify)
  );

  return (
    <aside
      className="hidden lg:flex flex-col w-64 shrink-0 bg-white border-r border-slate-200 sticky top-0 self-start h-screen overflow-y-auto"
      aria-label="Navigation latérale"
    >
      <EntitySelector />
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {/* Accueil */}
        <NavLink
          to="/"
          end
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
        >
          <Home size={16} aria-hidden />
          Accueil
        </NavLink>

        {/* Transactions (raccourci vers /depenses/transactions ; les Catégories & règles
            restent accessibles via l'onglet à l'intérieur de la page) */}
        <NavLink
          to="/depenses/transactions"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
        >
          <Receipt size={16} aria-hidden />
          Transactions
        </NavLink>

        {/* Cash-flow */}
        <NavLink
          to="/cash-flow"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
        >
          <LineChart size={16} aria-hidden />
          Cash-flow
        </NavLink>

        {/* À payer */}
        <NavLink
          to="/a-payer"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
        >
          <CalendarClock size={16} aria-hidden />
          À payer
        </NavLink>

        {/* Factures clients */}
        <NavLink
          to="/factures"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
        >
          <FileText size={16} aria-hidden />
          Factures clients
        </NavLink>

        {/* Prévisionnels */}
        <NavLink
          to="/previsionnels"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
        >
          <Wallet size={16} aria-hidden />
          Prévisionnels
        </NavLink>

        {/* Employés */}
        <NavLink
          to="/employes"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
        >
          <Users size={16} aria-hidden />
          Employés
        </NavLink>

        {/* Dettes */}
        <NavLink
          to="/dettes"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
        >
          <Banknote size={16} aria-hidden />
          Dettes
        </NavLink>

        {/* TVA */}
        <NavLink
          to="/tva"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
        >
          <Percent size={16} aria-hidden />
          TVA
        </NavLink>

        {/* Import justificatifs */}
        <NavLink
          to="/justificatifs"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
        >
          <ScanLine size={16} aria-hidden />
          Justificatifs
        </NavLink>

        {/* Refacturation intra-groupe — visible seulement si ≥ 2 entités
            flaguées include_in_group (sinon rien à consolider). */}
        {showRefacturation && (
          <NavLink
            to="/refacturation"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`
            }
          >
            <Repeat2 size={16} aria-hidden />
            Refacturation
          </NavLink>
        )}

        {/* Ventes Shopify — visible seulement si l'entité active (ou, en vue
            groupe, au moins une entité du groupe) a Shopify configuré. */}
        {showVentes && (
          <NavLink
            to="/ventes"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`
            }
          >
            <ShoppingBag size={16} aria-hidden />
            Ventes
          </NavLink>
        )}

        {/* Caisse */}
        <NavLink
          to="/caisse"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
        >
          <Coins size={16} aria-hidden />
          Caisse
        </NavLink>

        {/* Associés (CCA) */}
        <NavLink
          to="/associes"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
        >
          <Users size={16} aria-hidden />
          Associés
        </NavLink>

        {/* Paramètres */}
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
        >
          <Settings size={16} aria-hidden />
          Paramètres
        </NavLink>
      </nav>

      {/* Admin — separated from business links */}
      <div className="border-t border-slate-100 px-3 py-3">
        <NavLink
          to="/admin/entities"
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-slate-800 text-white'
                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
            }`
          }
        >
          <Building2 size={16} aria-hidden />
          Admin entités
        </NavLink>
      </div>
    </aside>
  );
}
